import React from 'react';
import {
  Autocomplete, TextField, Box, Typography, Paper,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  useTheme, alpha,
} from '@mui/material';

/**
 * SearchableSelect — the one dropdown used everywhere in the Fee module.
 *
 * Design brief
 * ------------
 * Match the elegant Select look used in the Paper Builder filter bar
 * (rounded outlined field, floating label, subtle chevron), but make the
 * popover a real search — type to filter, click to pick, keyboard-friendly.
 *
 * Built on top of `Autocomplete` because:
 *   - it already handles keyboard nav, filtering, and clearing
 *   - MUI `Select` cannot be made searchable without hacks
 *   - the resulting field is visually identical to `Select` at `size="small"`
 *
 * API
 * ---
 *   const [v, setV] = useState('');
 *   <SearchableSelect
 *     label="Session"
 *     value={v}
 *     onChange={setV}
 *     options={sessions.map(s => ({ value: s._id, label: s.name }))}
 *     placeholder="All sessions"
 *     clearable
 *   />
 */

export interface SelectOption {
  value: string;
  label: string;
  /** Optional secondary line rendered under the label in the dropdown. */
  secondary?: string;
  /**
   * Optional inline pill / icon rendered on the RIGHT of the label in the
   * dropdown row, AND appended to the field's input adornment when this
   * option is the currently-selected value. Use for status hints like
   * "Active" on a session picker.
   */
  badge?: React.ReactNode;
  disabled?: boolean;
}

export interface SearchableSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /** Show an "×" to clear the selection back to ''. Default: true. */
  clearable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  size?: 'small' | 'medium';
  /**
   * When false (default), the field sizes to its `minWidth` and does NOT
   * grow inside a flex container — matching the layout behaviour of the
   * old `<FormControl sx={{ minWidth: 200 }}>` filter dropdowns. Explicitly
   * set to true only in dialog forms where the field should stretch to
   * fill its row.
   */
  fullWidth?: boolean;
  /** Fixed min-width for the field. Default: 200. */
  minWidth?: number | string;
  /** Optional text shown below the field. */
  helperText?: string;
  error?: boolean;
  /** Overrides the default filter — pass to allow multi-key matching. */
  filterFn?: (option: SelectOption, input: string) => boolean;
  /** Rendered before the label in each option row (e.g. an icon). */
  renderOptionAdornment?: (option: SelectOption) => React.ReactNode;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  clearable = true,
  disabled,
  loading,
  size = 'small',
  fullWidth = false,
  minWidth = 200,
  helperText,
  error,
  filterFn,
  renderOptionAdornment,
}) => {
  const theme = useTheme();

  // Resolve the current selected option object from the value string. Missing
  // matches render as "" so the field doesn't crash on stale ids.
  const selected = React.useMemo(
    () => options.find(o => o.value === value) ?? null,
    [options, value],
  );

  const defaultFilter = (opts: SelectOption[], state: any) => {
    const input = state.inputValue?.toLowerCase()?.trim() ?? '';
    if (!input) return opts;
    if (filterFn) return opts.filter(o => filterFn(o, input));
    return opts.filter(o =>
      o.label.toLowerCase().includes(input)
      || (o.secondary?.toLowerCase().includes(input) ?? false),
    );
  };

  return (
    <Autocomplete<SelectOption, false, boolean, false>
      value={selected}
      onChange={(_, v) => onChange(v?.value ?? '')}
      options={options}
      getOptionLabel={o => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      getOptionDisabled={o => Boolean(o.disabled)}
      loading={loading}
      disabled={disabled}
      disableClearable={!clearable}
      size={size}
      fullWidth={fullWidth}
      filterOptions={defaultFilter}
      // The popover matches `elegantMenuProps` — subtle border, soft shadow.
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            mt: 0.5,
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 24px rgba(0, 0, 0, 0.45)'
              : '0 8px 24px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
            '& .MuiAutocomplete-listbox': {
              py: 0.5,
              '& .MuiAutocomplete-option': {
                minHeight: 34,
                py: 0.75,
                px: 1.5,
                fontSize: '0.825rem',
                fontWeight: 500,
                gap: 1,
                transition: 'background-color 80ms ease',
                '&[aria-selected="true"]': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&.Mui-focused': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                },
              },
            },
          },
        },
      }}
      PaperComponent={Paper}
      sx={{ minWidth }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true, ...(params.InputLabelProps || {}) }}
          InputProps={{
            ...params.InputProps,
            // Slot the selected option's badge (if any) before Autocomplete's
            // own end adornment (clear / dropdown arrow). Kept flex so the
            // badge sits next to the value, not stacked over it.
            endAdornment: selected?.badge ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                  {selected.badge}
                </Box>
                {params.InputProps.endAdornment}
              </Box>
            ) : (
              params.InputProps.endAdornment
            ),
          }}
        />
      )}
      renderOption={(props, option: SelectOption, state: AutocompleteRenderOptionState) => (
        <Box
          component="li"
          {...props}
          key={option.value}
          sx={{ '&.MuiAutocomplete-option': { alignItems: 'flex-start' } }}
        >
          {renderOptionAdornment && (
            <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.25 }}>
              {renderOptionAdornment(option)}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={state.selected ? 600 : 500}
              sx={{ fontSize: '0.825rem', lineHeight: 1.3 }}
              noWrap
            >
              {option.label}
            </Typography>
            {option.secondary && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.15 }}
                noWrap
              >
                {option.secondary}
              </Typography>
            )}
          </Box>
          {option.badge && (
            <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.25, ml: 1 }}>
              {option.badge}
            </Box>
          )}
        </Box>
      )}
    />
  );
};

export default SearchableSelect;
